import { addDays, eachDayOfInterval, getDay, parseISO } from "date-fns";
import { toISODate } from "@/lib/format";

/** Monday-first week index (0 = måndag … 6 = sundag). */
export const weekdayIndex = (d) => (getDay(d) + 6) % 7;

/**
 * A weekly practice as it actually happens on its ORIGINAL occurrence date `iso`, after the per-date
 * exceptions stored on the series (skip_dates, overrides keyed by original date). Returns null when
 * the date is not an occurrence or it is cancelled. `date` is where it is shown (may be moved).
 */
export const occurrenceOf = (r, iso) => {
  if (weekdayIndex(parseISO(iso)) !== r.weekday) return null;
  if (r.skip_dates?.includes(iso)) return null;
  const o = r.overrides?.[iso] || null;
  return {
    occurrence_date: iso,
    date: o?.date || iso,
    start_time: o?.start_time || r.start_time,
    end_time: o?.end_time || r.end_time,
    room_id: o?.room_id || r.room_id,
    title: o?.title || r.title,
    notes: o?.notes || "",
    overridden: !!o,
  };
};

/** All occurrences of `recurring` shown between `from` and `to` (inclusive ISO), incl. ones moved into the window.
 *  `roomId` keeps only occurrences that actually happen in that room (series room or the room of an override). */
export const occurrencesBetween = (recurring = [], from, to, roomId = null) => {
  const out = [];
  if (!from || !to) return out;
  const room = roomId && roomId !== "all" ? roomId : null;
  // originals up to 31 days outside the window may be moved into it
  const days = eachDayOfInterval({ start: addDays(parseISO(from), -31), end: addDays(parseISO(to), 31) });
  for (const r of recurring) {
    if (!r.active) continue;
    for (const d of days) {
      const occ = occurrenceOf(r, toISODate(d));
      if (occ && occ.date >= from && occ.date <= to && (!room || occ.room_id === room)) out.push({ practice: r, ...occ });
    }
  }
  return out;
};

/** Practices (merged with their occurrence) that happen on `day` (Date), optionally only in `roomId`. */
export const practicesOn = (recurring = [], day, roomId = null) => {
  const iso = toISODate(day);
  return occurrencesBetween(recurring, iso, iso, roomId).map(({ practice, ...occ }) => ({ ...practice, ...occ, series_id: practice.id }));
};

/** Does the series ever happen in `roomId` – as its rule or through a per-date room override? */
export const practiceTouchesRoom = (r, roomId) => r.room_id === roomId || Object.values(r.overrides || {}).some((o) => o.room_id === roomId);
